import * as React from "react";
import { useTheme, type ThemeChoice } from "./theme";
import { cn } from "./cn";

const CHOICES: { key: ThemeChoice; label: string; glyph: string }[] = [
  { key: "light", label: "Light", glyph: "☀" },
  { key: "dark", label: "Dark", glyph: "☾" },
  { key: "system", label: "Match system", glyph: "◐" },
];

/**
 * ThemeToggle — the control for a theme system that already worked everywhere.
 *
 * ThemeProvider wraps all three consoles and resolves the system preference, but useTheme() had
 * exactly ONE caller: the admin Topbar. So the admin console could choose and the account and
 * mailbox consoles silently followed the operating system with no way to override — which reads
 * as "there is no dark mode" from the only two consoles most people ever see.
 *
 * Deliberately not the admin's Radix dropdown lifted wholesale: that carries a menu dependency
 * into a shared package for three buttons. A segmented control needs nothing, and — unlike a
 * dropdown — cannot be clipped by an overflow ancestor, which is the fault that had just made
 * the notification bell invisible in this same header.
 *
 * It shows which choice is ACTIVE, not merely which theme is showing. "Match system" and a dark
 * system resolve to the same pixels and are not the same setting, and a control that cannot tell
 * them apart leaves someone unable to see why their theme changes when their laptop does.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { choice, setChoice } = useTheme();
  return (
    <div
      role="group"
      aria-label="Theme"
      className={cn("inline-flex items-center gap-0.5 rounded-lg border border-line p-0.5", className)}
    >
      {CHOICES.map((c) => (
        <button
          key={c.key}
          type="button"
          title={c.label}
          aria-label={c.label}
          aria-pressed={choice === c.key}
          onClick={() => setChoice(c.key)}
          className={
            "grid size-7 place-items-center rounded-md text-[13px] leading-none transition-colors pointer-coarse:size-10 " +
            (choice === c.key ? "bg-surface2 text-fg" : "text-muted hover:text-fg")
          }
        >
          {c.glyph}
        </button>
      ))}
    </div>
  );
}
